import { useState } from "react";
import { CheckCircle, CircleDashed, CircleHalf, Question, SealCheck, MapPin } from "@phosphor-icons/react";
import { Thumb } from "@/components/dailylogs/DlMediaViewer";
import { ItemEditor } from "@/components/dailylogs/DlEditors";

export const ICONS = {
  reported_complete: [CheckCircle, "text-emerald-500", "Completed"],
  verified: [SealCheck, "text-emerald-600", "Verified complete"],
  partial: [CircleHalf, "text-[#e9d99a]", "Partially complete"],
  needs_review: [Question, "text-[#ff8a3d]", "Needs review"],
  assigned: [CircleDashed, "text-slate-400", "Assigned"],
};

const DONE = ["reported_complete", "verified"];

function Line({ it, done, hl, onSaved }) {
  const [, , label] = ICONS[it.status] || ICONS.assigned;
  const matched = DONE.includes(it.status);
  const I = done ? (it.status === "verified" ? SealCheck : CheckCircle) : matched ? CheckCircle : it.status === "needs_review" ? Question : it.status === "partial" ? CircleHalf : CircleDashed;
  const cls = done || matched ? "text-emerald-500" : it.status === "needs_review" ? "text-[#ff8a3d]" : it.status === "partial" ? "text-[#e9d99a]" : "text-[#efe4b0]";
  const who = done ? (it.reported_by || []).join(", ") : "";
  const unit = it.unit ? `Unit ${it.unit} — ` : "";
  return (
    <ItemEditor it={it} onSaved={onSaved}>
      <button className={`w-full flex items-start gap-1.5 text-left text-[12.5px] leading-snug py-0.5 px-1 rounded hover:bg-slate-100 transition-colors ${hl === it.id ? "ring-2 ring-[#327de3] bg-[#327de3]/10" : ""}`}
        title={`${label}${it.note ? ` — ${it.note}` : ""} · click to correct`} data-testid={`dl-line-${done ? "done" : "asg"}-${it.id}`} id={done ? undefined : `dl-item-${it.id}`}>
        <I size={14} weight={done || matched || it.status !== "assigned" ? "fill" : "regular"} className={`${cls} mt-[1px] shrink-0`} />
        <span className={`flex-1 ${done ? "text-emerald-600" : "text-[#efe4b0]"}`}>
          {unit}{it.task}{who && <span className="text-slate-400"> — {who}</span>}
          {it.status === "needs_review" && <span className="text-[#ff8a3d] font-semibold"> · Needs review</span>}
          {done && it.unassigned && <span className="text-slate-400 italic"> · not matched to an assignment</span>}
          {!done && matched && it.completed_date && it.completed_date !== it.date && <span className="text-emerald-500"> (completed {it.completed_date})</span>}
        </span>
        {it.manual && <span className="text-[8px] uppercase font-bold text-emerald-600">edited</span>}
      </button>
    </ItemEditor>
  );
}

export function Block({ b, crew, hl, onMedia, onSaved }) {
  const [all, setAll] = useState(false);
  const [who, setWho] = useState(null);
  const senders = [...new Set(b.media.map((m) => m.sender_label).filter(Boolean))];
  const media = who ? b.media.filter((m) => m.sender_label === who) : b.media;
  const shown = all ? media : media.slice(0, 8);
  const review = b.assigned.filter((i) => ["needs_review", "partial"].includes(i.status)).length;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-1.5" data-testid={`dl-block-${b.key}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 min-w-0"><MapPin size={12} weight="fill" className={b.site_id ? "text-[#327de3]" : "text-[#e9d99a]"} />
          <span className="text-[13px] font-bold text-slate-900 truncate">{b.label}</span>
          {crew && <span className="text-[11px] text-slate-400 truncate">· {crew}</span>}
          {b.site_id && b.confidence === "medium" && <span className="text-[9px] uppercase font-bold text-[#a78bfa] ml-1">inferred</span>}</div>
        <div className="text-[10px] font-semibold text-slate-500 flex flex-wrap gap-x-1"><span className="text-[#efe4b0]">Assigned {b.assigned.length}</span> <span className="text-slate-300">|</span> <span className="text-emerald-500">Completed {b.completed.length}</span>
          {review > 0 && <> <span className="text-slate-300">|</span> <span className="text-[#ff8a3d]">Review {review}</span></>}</div>
      </div>
      {b.assigned.length > 0 && <div data-testid={`dl-block-assigned-${b.key}`}>
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#efe4b0]">Assigned</div>
        {b.assigned.map((it) => <Line key={`a${it.id}`} it={it} hl={hl} onSaved={onSaved} />)}
      </div>}
      {b.completed.length > 0 && <div data-testid={`dl-block-completed-${b.key}`}>
        <div className="flex items-center gap-2 flex-wrap"><span className="text-[9px] uppercase tracking-widest font-bold text-emerald-500">Completed</span>
          <span className="text-[9px] text-slate-400 inline-flex items-center gap-1" data-testid={`dl-legend-${b.key}`}><CheckCircle size={10} weight="fill" className="text-emerald-500" />= completed <CircleDashed size={10} className="text-[#efe4b0] ml-1" />= not yet confirmed</span></div>
        {b.completed.map((it) => <Line key={`d${it.id}`} it={it} done hl={hl} onSaved={onSaved} />)}
      </div>}
      {b.media.length > 0 && senders.length > 0 && <div className="flex flex-wrap gap-1 pt-0.5" data-testid={`dl-block-senders-${b.key}`}>
        {senders.length > 1 && <button type="button" onClick={() => setWho(null)} className={`h-5 px-2 rounded-full text-[10px] font-semibold border ${!who ? "bg-[#327de3] border-[#327de3] text-white" : "border-slate-200 text-slate-500"}`} data-testid={`dl-sender-all-${b.key}`}>All {b.media.length}</button>}
        {senders.map((s) => {
          const n = b.media.filter((m) => m.sender_label === s).length;
          const unk = s.startsWith("Unknown");
          return <button key={s} type="button" onClick={() => setWho(who === s ? null : s)} title={unk ? "This WhatsApp sender isn't mapped to an employee yet (Daily Logs settings → WhatsApp senders)" : `Photos sent by ${s}`}
            className={`h-5 px-2 rounded-full text-[10px] font-semibold border ${who === s ? "bg-[#327de3] border-[#327de3] text-white" : unk ? "border-[#ff8a3d]/50 text-[#ff8a3d]" : "border-slate-200 text-slate-600"}`}
            data-testid={`dl-sender-${b.key}-${s.replace(/\W+/g, "-")}`}>{s} · {n}</button>;
        })}
      </div>}
      {media.length > 0 && <div className="flex flex-wrap gap-1 pt-0.5">
        {shown.map((m) => (
          <div key={m.id} className="relative" title={`Sent by ${m.sender_label}${m.sender_name ? ` (WhatsApp name: ${m.sender_name})` : ""}`}>
            <Thumb m={m} className={`w-11 h-11 ${hl === m.id ? "ring-2 ring-[#327de3]" : ""}`} onClick={() => onMedia(media, m)} />
            <span className={`absolute bottom-0 inset-x-0 truncate rounded-b px-0.5 text-[7px] font-bold leading-tight text-white ${m.sender_employee ? "bg-black/55" : "bg-[#ff8a3d]/85"}`} data-testid={`dl-media-sender-${m.id}`}>{m.sender_employee || "Unknown"}</span>
          </div>
        ))}
        {media.length > 8 && <button onClick={() => setAll(!all)} className="w-11 h-11 rounded bg-slate-100 text-[10px] font-bold text-slate-500 hover:bg-slate-200" data-testid={`dl-block-more-${b.key}`}>{all ? "less" : `+${media.length - 8}`}</button>}
      </div>}
    </div>
  );
}

export function CrewCard({ c, hl, onMedia, onSaved }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 space-y-2 break-inside-avoid mb-3" data-testid={`dl-crew-${c.crew}`}>
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        <div className="text-sm font-bold text-slate-900">{c.crew}</div>
        <div className="text-[10px] font-semibold text-slate-500">{c.blocks.length} site{c.blocks.length === 1 ? "" : "s"} · {c.completed}/{c.assigned} done</div>
      </div>
      {c.blocks.map((b) => <Block key={b.key} b={b} hl={hl} onMedia={onMedia} onSaved={onSaved} />)}
    </div>
  );
}

export function bySite(crews) {
  const sites = {};
  crews.forEach((c) => c.blocks.forEach((b) => {
    const k = b.site_id || "none";
    const s = sites[k] || (sites[k] = { key: k, site: b.site, blocks: [] });
    s.blocks.push({ ...b, crew: c.crew, key: `${c.crew}|${b.key}` });
  }));
  return Object.values(sites).sort((a, b) => (a.key === "none") - (b.key === "none") || b.blocks.length - a.blocks.length);
}

export function SiteCard({ s, hl, onMedia, onSaved }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 space-y-2 break-inside-avoid mb-3" data-testid={`dl-site-card-${s.key}`}>
      <div className="text-sm font-bold text-slate-900 px-0.5">{s.site}</div>
      {s.blocks.map((b) => <Block key={b.key} b={{ ...b, label: b.unit ? `Unit ${b.unit}` : b.site }} crew={b.crew} hl={hl} onMedia={onMedia} onSaved={onSaved} />)}
    </div>
  );
}
